<template>
  <div class="custom-select" :class="{ disabled }">
    <!-- Trigger -->
    <div class="custom-select__trigger" @click="toggleDropdown">
      <span>{{ selectedLabel }}</span>
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M15 7.5L10 13.75L5 7.5H15Z" fill="#DE4527" />
      </svg>
    </div>

    <!-- Dropdown -->
    <div v-if="open" class="custom-select__dropdown">
      <div v-for="(option, index) in options" :key="option.key" class="custom-select__option" :class="{
        selected: option.key === internalValue,
        'show-foam-badge': option.key === foamBadgeKey,
        'top-position': index < 4
      }" :data-has-foam="option.hasFoam" @click="selectOption(option)" @mouseenter="handleOptionHover(option)">
        {{ option.value }}

        <!-- Foam badge -->
        <div v-if="!option.hasFoam" class="foam-badge" :class="badgePosition">
          <button class="foam-badge__close" @click.stop="closeFoamBadge">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M6.8484 6L12 0.8484L11.1516 0L6 5.1516L0.8484 0L0 0.8484L5.1516 6L0 11.1516L0.8484 12L6 6.8484L11.1516 12L12 11.1516L6.8484 6Z"
                fill="black" />
            </svg>
          </button>
          <div class="foam-badge__text">
            No foam cushions available for this year/model
          </div>
          <button class="foam-badge__link" @click.stop="selectSeatOption(option)">
            See Seat Covers
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CustomSelect",

  props: {
    modelValue: {
      type: [String, Number],
      default: "",
    },
    options: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      default: "Select option",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      open: false,
      internalValue: this.modelValue,
      foamBadgeKey: null,
      badgePosition: "bottom",
      scrollTimeout: null, // 🧠 store debounce timer
    };
  },

  computed: {
    selectedLabel() {
      const found = this.options.find((o) => o.key === this.internalValue);
      return found ? found.value : this.placeholder;
    },
  },

  watch: {
    modelValue(newVal) {
      this.internalValue = newVal;
    },
  },

  methods: {
    toggleDropdown() {
      if (this.disabled) return;
      this.open = !this.open;

      this.$nextTick(() => {
        const dropdown = this.$el.querySelector(".custom-select__dropdown");
        if (!dropdown) return;

        // When dropdown opens
        if (this.open) {
          this.adjustFoamBadgePosition();
          dropdown.addEventListener("scroll", this.handleScrollDebounced);
        } else {
          dropdown.removeEventListener("scroll", this.handleScrollDebounced);
        }
      });
    },

    handleOptionHover(option) {
      // When hovering an option that has no foam
      if (!option.hasFoam) {
        this.foamBadgeKey = option.key;
        // Debounce hover too — avoids spam on quick moves
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.adjustFoamBadgePosition(option);
        }, 100);
      }
    },

    // 🧠 Debounced scroll handler
    handleScrollDebounced() {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.adjustFoamBadgePosition();
      }, 200); // ← wait 200ms after scroll stops
    },

    adjustFoamBadgePosition() {
      this.$nextTick(() => {
        const dropdown = this.$el.querySelector(".custom-select__dropdown");
        if (!dropdown) return;

        const dropdownRect = dropdown.getBoundingClientRect();
        const visibleTop = dropdownRect.top;
        const visibleBottom = dropdownRect.bottom;

        const badges = dropdown.querySelectorAll(".foam-badge");
        badges.forEach((badge) => {
          const badgeRect = badge.getBoundingClientRect();

          // Decide position
          if (badgeRect.bottom > visibleBottom) {
            badge.classList.add("top");
            badge.classList.remove("bottom");
          } else if (badgeRect.top < visibleTop) {
            badge.classList.add("bottom");
            badge.classList.remove("top");
          } else {
            // badge.classList.remove("top", "bottom");
          }
        });
      });
    },

    selectOption(option) {
      this.internalValue = option.key;
      this.$emit("update:modelValue", option.key);
      this.$emit('option-changed', option);

      if (!option.hasFoam) {
        this.foamBadgeKey = option.key;
        this.$nextTick(() => this.adjustFoamBadgePosition());
        this.open = false;
      } else {
        this.foamBadgeKey = null;
        this.open = false;
      }
    },

    selectSeatOption(option) {
      this.internalValue = option.key;
      this.$emit("update:modelValue", option.key);
      this.$emit('option-changed', option);
      this.foamBadgeKey = null;
      this.open = false;
    },

    closeFoamBadge() {
      this.foamBadgeKey = null;
      this.open = false;
    },
  },
};
</script>

<style scoped></style>
